// eslint-disable-next-line
import React from 'react'
import {
  Modal, Text, Row, Col,
} from '@csc/dls'
import styles from './LoadingModal.module.scss'

const mobileLogoSvg = 'https://assets.onekingslane.com/m/62954f1b3cd8439b/original/OKL2018_Elephant_K.png'
interface ProgressingOutlineProps {
  children?: React.ReactNode;
  mt?: number;
}

const ProgressingOutline: React.FC<ProgressingOutlineProps> = ({ children, mt = 6 }) => (
  <div className={`${styles.progressCircle} mt-${mt} ml-auto mr-auto`}>
    <div className={styles.ring}>
      <div />
    </div>
    <div className={styles.logo}>
      {children}
    </div>
  </div>
)

interface LogoWithProgressingOutlineProps {
  mt?: number;
}

const LogoWithProgressingOutline: React.FC<LogoWithProgressingOutlineProps> = ({ mt = 6 }) => (
  <ProgressingOutline mt={mt}>
    <img src={mobileLogoSvg} alt="OKL" />
  </ProgressingOutline>
)

/**
 * Displays a loading modal when it's `open` prop is true
 * It shows which step the user is currently on based on "currentStep" and "totalSteps"
 * props. See LoadingModal.propTypes for props reference
 *
 * Prerequisite: Must be wrapped inside the context provider defined in
 * hooks/useMobileLogo.js
 *
 * @param {Object} props - React props
 * @param {boolean} props.open - Control whether the modal is closed or open
 * @param {string} props.message - Indicate how many steps there are in total
 *
 * @example
 * () => {
 *   const [loading, setLoading] = useState(false)
 *   return <div>
 *     <LoadingModal open={loading} currentStep={1} totalSteps={3}/>
 *     <button onClick={()=>{setLoading(true)}}>Open loading</button>
 *   </div>
 * }
 */
interface LoadingModalProps {
  open: boolean;
  message?: string;
  hasLogo?: boolean;
  variant?: 'light';
  closeable?: boolean;
  loading?: boolean;
  onClose?: () => void;
}

const LoadingModal: React.FC<LoadingModalProps> = ({
  open,
  message,
  hasLogo = true,
  variant = 'light',
  closeable = false,
  loading = true,
  onClose = () => {},
}) => {
  if (!open) {
    return null
  }
  const loadingDisplay = () => (hasLogo
    ? <LogoWithProgressingOutline mt={message ? 6 : 0} />
    : <ProgressingOutline />)
  return (
    <div className={styles.overwriteModalZIndex}>
      <Modal
        open={open}
        variant={variant}
        closeable={closeable}
        closeButton={closeable}
        onClose={onClose}
      >
        {open && (
          <Row cols={1} className="p-6">
            <Col>
              {!!message && <Text className="text-center">{message}</Text>}
              {loading && loadingDisplay()}
            </Col>
          </Row>
        )}
      </Modal>
    </div>
  )
}

export default LoadingModal
